import { Fragment, useCallback, useEffect, useState } from "react";
import { T, Bone } from "./reportTokens";
import { Icon, SectionCard, SegmentBadge, Avatar, useMoney, PaginationBar, HoverTip, PageSizeSelect, CustomerSearchBox } from "./customersReportShared";
// The segment palette is keyed by DISPLAY name, and `segmentName` from the API
// matches the local SEGMENTS names exactly - that is a documented contract.
import { segmentColor, channelIcon, CUST_CONFIG } from "./customersReportData";
import type { CustomerDetailsResponse, CustomerOrder, CustomerOrderLog, OrderLogSortKey } from "../../interface/customersReportInterface";
import { ReportSectionExportButton } from "./components/ReportExportButton";
import { formatMoneyForExport } from "../../utils/currency";

/* ═══════════════════════════════════════════════════════════════════════════
   LIVE CUSTOMER PROFILE (CUST-7 / CUST-16 / CUST-17)

   Renders the `customer-details-drilldowns` response directly rather than
   mapping it onto the mock `CustomerRecord`. The mock carries fields the API
   has no source for (loyalty tier, per-item modifiers) and is missing ones the
   API does have (net spend, void vs refund, cancel reasons); mapping between
   them would have to invent or discard on every render. `CustomerProfile` in
   customersReportViews.tsx still renders the mock for demo mode.
   ═══════════════════════════════════════════════════════════════════════════ */

const thMini: React.CSSProperties = {
    fontSize: 10, fontWeight: 600, color: T.t3, textAlign: "left",
    padding: "8px 10px", textTransform: "uppercase", letterSpacing: 0.3,
    whiteSpace: "nowrap", borderBottom: `1px solid ${T.divider}`,
};
const tdMini: React.CSSProperties = {
    padding: "9px 10px", fontSize: 12.5, color: T.t1, whiteSpace: "nowrap",
};

/** A date the API may not have — "—" rather than an empty cell or "Invalid Date". */
const day = (iso: string | null): string => {
    if (!iso) return "—";
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime())
        ? iso
        : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

/**
 * An order's whole discount, as the POS bill shows it (QA #4): the order-level
 * `discount` PLUS the item-level `offerDiscount` and `loyaltyDiscount`. The API
 * sends the three separately; showing `discount` alone dropped every item-level
 * offer (order #000200: $10 shown, $22.30 on the bill).
 */
const totalDiscount = (o: CustomerOrder): number =>
    (o.discount || 0) + (o.offerDiscount || 0) + (o.loyaltyDiscount || 0);

/**
 * `order_time` arrives as 24-hour "HH:MM:SS". Shown as 12-hour with AM / PM
 * (QA #66: "03:23" gave no meridiem), seconds dropped as noise in a log.
 * Parsed as text, never through `Date`, so no timezone can shift it.
 * Used by the Order Log, the Visit Timeline and the Order Log export.
 */
const clock = (time: string | null): string => {
    const m = time ? /^(\d{1,2}):(\d{2})/.exec(time.trim()) : null;
    if (!m) return time ? time : "—";
    const h24 = Number(m[1]);
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${h12}:${m[2]} ${h24 < 12 ? "AM" : "PM"}`;
};

function Stat({ label, value, hint, tone }: {
    label: string; value: string; hint?: string; tone?: string;
}) {
    return (
        <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10.5, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: tone ?? T.t1, marginTop: 2 }}>{value}</div>
            {hint && <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>{hint}</div>}
        </div>
    );
}

/** CUST-7.1's lifetime mini-card (QA #13) — a card of its own, not a summary figure. */
function MiniCard({ label, value }: { label: string; value: string }) {
    return (
        <div style={{ minWidth: 0, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: 10.5, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.t1, marginTop: 4 }}>{value}</div>
        </div>
    );
}

/** CUST-17's R/F/M dots — a 1–5 score is not self-explanatory as a bare number. */
function ScoreDots({ score }: { score: number }) {
    return (
        <span style={{ display: "inline-flex", gap: 3, marginLeft: 6 }}>
            {[1, 2, 3, 4, 5].map((i) => (
                <span key={i} style={{
                    width: 7, height: 7, borderRadius: "50%",
                    background: i <= score ? T.blue : T.divider,
                }} />
            ))}
        </span>
    );
}

/**
 * One order's item lines with CUST-17's refund / void detail. Shared by the Order
 * Log's expanded row and a Visit Timeline node (CUST-7.2: "same content as
 * CUST-7.3 for that specific order"), so the two never describe an order
 * differently.
 */
function OrderItemsDetail({ order, money }: { order: CustomerOrder; money: (n: number) => string }) {
    if (order.items.length === 0) {
        return <div style={{ fontSize: 12, color: T.t3, fontStyle: "italic" }}>Item details unavailable.</div>;
    }
    return (
        <>
            {order.items.map((item, i) => (
                <div key={i} style={{
                    display: "flex", justifyContent: "space-between", gap: 12,
                    fontSize: 12, padding: "3px 0",
                    color: item.refunded ? T.red : item.cancelled ? T.orange : T.t2,
                }}>
                    <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                        {item.name} ×{item.quantity}
                        {item.categoryName && <span style={{ color: T.t3 }}> · {item.categoryName}</span>}
                        {/* Refunded and voided are different events and read differently. */}
                        {item.refunded && ` — refunded${item.reason ? ` ("${item.reason}")` : ""}${item.staffName ? `, by ${item.staffName}` : ""}`}
                        {!item.refunded && item.cancelled && ` — voided before payment${item.reason ? ` (${item.reason})` : ""}`}
                    </span>
                    <span style={{ whiteSpace: "nowrap" }}>{money(item.price)}</span>
                </div>
            ))}
        </>
    );
}

function OrderRow({ order, money }: { order: CustomerOrder; money: (n: number) => string }) {
    const [open, setOpen] = useState(false);
    const hasItems = order.items.length > 0;
    const itemsText = order.items.map((i) => `${i.name} ×${i.quantity}`).join(", ");

    const cancelCell =
        order.cancellation === "full"
            ? <span style={{ color: T.red, fontWeight: 600 }}>Cancelled</span>
            : order.cancellation === "partial"
                ? <span style={{ color: T.orange, fontWeight: 600 }}>Partial</span>
                : "—";

    return (
        <Fragment>
            <tr onClick={() => hasItems && setOpen(!open)}
                style={{ borderBottom: `1px solid ${T.divider}`, cursor: hasItems ? "pointer" : "default" }}>
                <td style={tdMini}>{day(order.date)}</td>
                <td style={tdMini}>{clock(order.time)}</td>
                <td style={tdMini}>{order.orderNo}</td>
                <td style={tdMini}>{order.channel ?? "—"}</td>
                <td style={{ ...tdMini, maxWidth: 260, whiteSpace: "normal" }}>
                    {hasItems
                        /* Two lines at most, ellipsis after, full list in the report's
                           own tooltip on hover (QA #14). `anywhere` lets a name with no
                           spaces break instead of running into the Item Total column. */
                        ? <HoverTip text={itemsText} style={{
                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                            overflow: "hidden", overflowWrap: "anywhere",
                        }}>{itemsText}</HoverTip>
                        /* Empty items is normal for a 3P order, not an error. */
                        : <span style={{ color: T.t3, fontStyle: "italic" }}>item details unavailable</span>}
                </td>
                <td style={{ ...tdMini, fontWeight: 600 }}>{money(order.itemTotal)}</td>
                <td style={tdMini}>{totalDiscount(order) ? `-${money(totalDiscount(order))}` : "—"}</td>
                <td style={{ ...tdMini, fontWeight: 600 }}>{money(order.grandTotal)}</td>
                <td style={tdMini}>
                    {order.refundAmount
                        ? <span style={{ color: T.red }}>-{money(order.refundAmount)}</span>
                        : "—"}
                </td>
                <td style={tdMini}>{cancelCell}</td>
                <td style={tdMini}>{order.orderStatusLabel ?? "—"}</td>
            </tr>
            {open && hasItems && (
                <tr>
                    <td colSpan={11} style={{ padding: "8px 14px", background: T.bg }}>
                        <OrderItemsDetail order={order} money={money} />
                    </td>
                </tr>
            )}
        </Fragment>
    );
}

/**
 * CUST-7.1's At Risk alert: "This customer is overdue for a visit. Last seen 10
 * days ago — 1.5× their usual gap of 6.5 days." The ratio is the customer's
 * actual one (days away / usual gap), so it states how overdue they are.
 */
const atRiskLine = (daysAway: number | null, usualGap: number | null): string => {
    if (daysAway == null) return "This customer is overdue for a visit.";
    const seen = `Last seen ${daysAway} day${daysAway === 1 ? "" : "s"} ago`;
    return usualGap
        ? `This customer is overdue for a visit. ${seen} — ${(daysAway / usualGap).toFixed(1)}× their usual gap of ${usualGap} days.`
        : `This customer is overdue for a visit. ${seen}.`;
};

/**
 * CUST-17.1's Recency label ("Last visit yesterday") over CUST-3.1's
 * days_since_last_visit - calendar days on the stored date (QA #9), so an
 * order this morning is "today" whatever the hour.
 */
const recencyLabel = (days: number | null): string => {
    if (days == null) return "—";
    if (days === 0) return "Visited today";
    if (days === 1) return "Last visit yesterday";
    return `${days} days since last visit`;
};

/** Short weekday name for a timeline node ("Thu"). */
const weekday = (iso: string): string => {
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { weekday: "short" });
};

/**
 * CUST-7.2 Visit Timeline (QA #13) — every order, newest first, as a vertical
 * timeline: date, day of week, time, channel icon and total. Two orders on one
 * day (lunch + dinner) are separate nodes. A cancelled order stays, struck
 * through with a "Cancelled" badge. Tapping a node expands the same item detail
 * the Order Log shows for that order.
 *
 * It pages the same `customer-details-drilldowns` order log, 10 at a time and
 * always date-descending — independent of the Order Log's own sort and page,
 * which the user may have changed.
 */
function VisitTimeline({ fetchPage, totalOrders, visits, money }: {
    fetchPage: (page: number) => Promise<CustomerOrderLog>;
    totalOrders: number;
    /** CUST-7.1's Total Visits, for the subtitle (QA #48) - see the header there. */
    visits: number;
    money: (n: number) => string;
}) {
    const [orders, setOrders] = useState<CustomerOrder[]>([]);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(totalOrders);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [open, setOpen] = useState<string | null>(null);

    const load = useCallback(async (next: number) => {
        setLoading(true);
        setFailed(false);
        try {
            const log = await fetchPage(next);
            setOrders((prev) => (next === 1 ? log.content : [...prev, ...log.content]));
            setTotal(log.totalOrders);
            setPage(next);
        } catch {
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [fetchPage]);

    useEffect(() => { load(1); }, [load]);

    let body;
    if (orders.length === 0 && loading) {
        body = (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} style={{ display: "flex", gap: 12, alignItems: "center" }}>
                        <Bone w={12} h={12} r={6} /><Bone w={170} h={12} r={4} /><Bone w={60} h={12} r={4} />
                    </div>
                ))}
            </div>
        );
    } else if (orders.length === 0 && failed) {
        body = (
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.red }}>
                <Icon name="error" size={15} color={T.red} />
                <span style={{ flex: 1 }}>Couldn't load the visit timeline.</span>
                <button onClick={() => load(1)} style={{ border: "none", background: "transparent", color: T.red, fontWeight: 600, cursor: "pointer", fontSize: 12 }}>Retry</button>
            </div>
        );
    } else if (orders.length === 0) {
        body = <div style={{ padding: "16px 8px", textAlign: "center", color: T.t3, fontSize: 12.5 }}>No visits yet.</div>;
    } else {
        body = (
            <div>
                {orders.map((o, i) => {
                    const cancelled = o.cancellation === "full";
                    const isOpen = open === o.orderId;
                    const last = i === orders.length - 1;
                    const strike = cancelled ? "line-through" : "none";
                    return (
                        <div key={o.orderId} style={{ display: "flex", gap: 12 }}>
                            {/* rail: dot + connecting line */}
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 12, flexShrink: 0 }}>
                                <span style={{ width: 10, height: 10, borderRadius: "50%", marginTop: 5, background: cancelled ? T.red : T.accent }} />
                                {!last && <span style={{ flex: 1, width: 2, background: T.divider, marginTop: 2 }} />}
                            </div>
                            <div style={{ flex: 1, minWidth: 0, paddingBottom: last ? 0 : 14 }}>
                                <div onClick={() => setOpen(isOpen ? null : o.orderId)}
                                    style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", cursor: "pointer" }}>
                                    <span style={{ fontSize: 12.5, fontWeight: 600, color: T.t1, textDecoration: strike }}>{day(o.date)}</span>
                                    <span style={{ fontSize: 12, color: T.t3 }}>{weekday(o.date)} · {clock(o.time)}</span>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: T.t2 }}>
                                        <Icon name={channelIcon(o.channel)} size={14} color={T.t3} />{o.channel ?? "—"}
                                    </span>
                                    <span style={{ marginLeft: "auto", fontSize: 12.5, fontWeight: 600, color: T.t1, textDecoration: strike }}>{money(o.grandTotal)}</span>
                                    {cancelled && <SegmentBadge label="Cancelled" color={T.red} />}
                                    {o.cancellation === "partial" && <SegmentBadge label="Partial" color={T.orange} />}
                                    <Icon name={isOpen ? "expand_less" : "expand_more"} size={16} color={T.t3} />
                                </div>
                                {isOpen && (
                                    <div style={{ marginTop: 8, padding: "8px 12px", background: T.bg, borderRadius: 8 }}>
                                        <div style={{ fontSize: 11, color: T.t3, marginBottom: 4 }}>
                                            Order {o.orderNo} · Item total {money(o.itemTotal)}
                                            {totalDiscount(o) ? ` · Discount -${money(totalDiscount(o))}` : ""}
                                            {o.refundAmount ? ` · Refunded -${money(o.refundAmount)}` : ""}
                                        </div>
                                        <OrderItemsDetail order={o} money={money} />
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
                {orders.length < total && (
                    <button onClick={() => load(page + 1)} disabled={loading}
                        style={{ marginTop: 12, padding: "7px 16px", borderRadius: 8, border: `1px solid ${T.cardBorder}`, background: T.surface, cursor: loading ? "default" : "pointer", fontSize: 12, fontWeight: 600, color: T.accent }}>
                        {loading ? "Loading…" : "Show earlier visits"}
                    </button>
                )}
                {failed && <div style={{ marginTop: 8, fontSize: 12, color: T.red }}>Couldn't load earlier visits — try again.</div>}
            </div>
        );
    }

    return (
        <SectionCard title="Visit Timeline" icon="timeline"
            /* QA #48: the subtitle counted ORDERS and called them visits, so it read
               "3 visits" beside a "Total Visits 2" mini-card on the same screen.
               CUST-7.2 frames each node as a visit ("two visits on the same date
               appear as separate nodes") while CUST-7.1's Total Visits is
               `visit_count` - non-cancelled visit DAYS. Both numbers are real and
               the story assumes they are one, so both are named rather than one
               being quietly renamed: "2 visits · 3 orders". They agree for most
               customers, and then only the visit count shows. */
            subtitle={`${visits.toLocaleString()} visit${visits === 1 ? "" : "s"}${
                total !== visits ? ` · ${total.toLocaleString()} order${total === 1 ? "" : "s"}` : ""
            } · newest first, full history`}>
            {body}
        </SectionCard>
    );
}

/** Order Log columns in display order, with the backend sort key each one uses (QA #20). Items has none. */
const ORDER_LOG_COLUMNS: { label: string; key: OrderLogSortKey | null }[] = [
    { label: "Date", key: "date" },
    { label: "Time", key: "time" },
    { label: "Order no", key: "orderNo" },
    { label: "Channel", key: "channel" },
    { label: "Items", key: null },
    { label: "Item Total", key: "itemTotal" },
    { label: "Discount", key: "discount" },
    { label: "Total", key: "grandTotal" },
    { label: "Refunded", key: "refundAmount" },
    /* CUST-7.3 lists these as two columns (QA #31). "Cancellation Status" is
       the one that was labelled "Status" and read "—" on every uncancelled
       order; "Order Status" is the backend's `orderStatusLabel`. Only the
       cancellation sorts — the backend's `status` sort key ranks cancelled >
       partial and has no ordering for the status label. */
    { label: "Cancellation Status", key: "status" },
    { label: "Order Status", key: null },
];

export function LiveCustomerProfile({ data, loading, error, onBack, onPage, segmentsComputed, fetchAllOrders, orderSort, onOrderSort, orderPageSize, onOrderPageSize, orderSearchInput, onOrderSearchInput, orderSearch, fetchVisitPage }: {
    data: CustomerDetailsResponse | null;
    loading: boolean;
    error: string | null;
    onBack: () => void;
    onPage: (page: number) => void;
    /** RFM is segment-derived, so it follows the Segment Breakdown's refresh. */
    segmentsComputed: boolean;
    /** The customer's whole order log, for the export. Omitted in demo mode. */
    fetchAllOrders?: () => Promise<CustomerOrder[]>;
    /** CUST-7.3 sort (QA #20) - applied server-side across the whole log. */
    orderSort?: { key: OrderLogSortKey; dir: "asc" | "desc" };
    onOrderSort?: (key: OrderLogSortKey) => void;
    /* QA #57 - rows per page and a search over the Order Log, both sent to the API.
       Omitted in demo mode, which has no endpoint to ask. */
    orderPageSize?: number;
    onOrderPageSize?: (n: number) => void;
    /** What is typed (immediate) / what was last sent (debounced). */
    orderSearchInput?: string;
    onOrderSearchInput?: (v: string) => void;
    orderSearch?: string;
    /** CUST-7.2 Visit Timeline: one date-descending page of the log. Omitted in demo mode. */
    fetchVisitPage?: (page: number) => Promise<CustomerOrderLog>;
}) {
    // Exact cents on the customer page (QA #4): the shared formatter rounds to
    // whole dollars, which turned a $23.50 bill into "$24" here - and the page's
    // job is to be checked against the POS order, line by line.
    const formatMoney = useMoney();
    const money = (n: number) => formatMoney(n, { decimals: true });
    const [reveal, setReveal] = useState(false);

    const back = (
        <button onClick={onBack} style={{
            display: "inline-flex", alignItems: "center", gap: 6, background: "transparent",
            border: "none", cursor: "pointer", color: T.t2, fontSize: 13, fontWeight: 600,
            padding: 0, alignSelf: "flex-start",
        }}>
            <Icon name="arrow_back" size={18} color={T.t2} /> Back
        </button>
    );

    // The whole page waits only for the FIRST load. A new page or sort of the
    // order log keeps the profile on screen and shimmers just the log (QA #20).
    if (!data && !error) {
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {back}
                <SectionCard>
                    <div style={{ padding: "28px 16px", textAlign: "center", color: T.t3, fontSize: 13 }}>
                        Loading customer…
                    </div>
                </SectionCard>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {back}
                <SectionCard>
                    <div style={{ padding: "28px 16px", textAlign: "center" }}>
                        <Icon name="person_search" size={28} color={T.t3} />
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.t1, marginTop: 10 }}>
                            Couldn't open this customer
                        </div>
                        <div style={{ fontSize: 12.5, color: T.t3, marginTop: 6, maxWidth: 440, margin: "6px auto 0", lineHeight: 1.5 }}>
                            {error ? "Something went wrong while loading this customer. Please try again." : "No profile came back for this row."}
                        </div>
                    </div>
                </SectionCard>
            </div>
        );
    }

    const { profile, rfm, orderLog } = data;
    const logLoading = loading;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {back}

            {/* ── identity ─────────────────────────────────────────────── */}
            <SectionCard>
                <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <Avatar name={profile.name ?? "?"} color={segmentColor(profile.segmentName)} size={48} />
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 18, fontWeight: 700, color: T.t1 }}>
                                {profile.name ?? "Unnamed customer"}
                            </span>
                            <SegmentBadge label={profile.segmentName} color={segmentColor(profile.segmentName)} />
                        </div>
                        <div style={{ fontSize: 12.5, color: T.t3, marginTop: 4, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                            {/* Contacts arrive both full and masked, so revealing costs no request. */}
                            <span>{reveal ? (profile.email ?? "—") : (profile.emailMasked ?? "—")}</span>
                            <span>·</span>
                            <span>{reveal ? (profile.phone ?? "—") : (profile.phoneLast4 ? `•••• ${profile.phoneLast4}` : "—")}</span>
                            <button onClick={() => setReveal(!reveal)} title={reveal ? "Hide contacts" : "Reveal contacts"}
                                style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, display: "inline-flex" }}>
                                {/* Same state language as the table headers (CUST-5.1): a crossed-out
                                    eye while masked, an open one while revealed (QA #14). */}
                                <Icon name={reveal ? "visibility" : "visibility_off"} size={15} color={reveal ? T.accent : T.t3} />
                            </button>
                            {/* CUST-7.1 header: the primary channel icon (QA #13). */}
                            {profile.primaryChannel && (
                                <>
                                    <span>·</span>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }} title="Primary channel">
                                        <Icon name={channelIcon(profile.primaryChannel)} size={15} color={T.t3} />
                                        {profile.primaryChannel}
                                    </span>
                                </>
                            )}
                        </div>
                        {/* CUST-7.1: "Member since {first_visit_date}" - the first visit at this outlet. */}
                        <div style={{ fontSize: 11.5, color: T.t3, marginTop: 6 }}>
                            Member since {day(profile.firstVisitHere)}
                        </div>
                    </div>
                </div>
            </SectionCard>

            {/* ── CUST-7.1 At Risk alert (QA #13) ──────────────────────── */}
            {profile.segmentKey === "at_risk" && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 14px", borderRadius: 10, background: T.orangeSoft, color: T.orange, fontSize: 12.5, lineHeight: 1.5 }}>
                    <Icon name="warning" size={16} color={T.orange} />
                    <span>{atRiskLine(rfm.daysSinceLastVisit, rfm.avgDaysBetweenVisits)}</span>
                </div>
            )}

            {/* ── CUST-7.1 lifetime summary row: exactly 4 mini-cards (QA #13) ──
                Refunds and net spend are not mini-cards - they belong to the
                Order Log's summary footer (CUST-7.3); the channel is in the header.
                4 across on desktop, 2 × 2 on a phone. */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
                <MiniCard label="Total Visits" value={profile.visits.toLocaleString()} />
                <MiniCard label="Total Spend" value={money(profile.totalSpend)} />
                {/* CUST-7.1: total_spend / visit_count; equals Total Spend for one visit. */}
                <MiniCard label="Avg Order Value" value={profile.avgOrderValue == null ? "—" : money(profile.avgOrderValue)} />
                {/* null for a one-visit customer: a gap is undefined, so "—" (CUST-7.1 edge case). */}
                <MiniCard label="Avg Days Between Visits" value={rfm.avgDaysBetweenVisits == null ? "—" : `${rfm.avgDaysBetweenVisits} days`} />
            </div>

            {/* ── RFM (CUST-17) ────────────────────────────────────────── */}
            <SectionCard title="RFM detail" subtitle={!segmentsComputed
                ? "Segments have never been computed — run Refresh Segments"
                // CUST-3.1 (QA #2): the scores below are the neutral 3/3/3.
                : rfm.insufficientData
                    ? "Insufficient data for segmentation — fewer than 5 customers at this outlet, so scores are neutral (3 / 3 / 3)"
                    : undefined}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16 }}>
                    <div>
                        <div style={{ fontSize: 10.5, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>
                            Recency<ScoreDots score={rfm.recencyScore} />
                        </div>
                        <div style={{ fontSize: 13, color: T.t2, marginTop: 4 }}>
                            {recencyLabel(rfm.daysSinceLastVisit)}
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: 10.5, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>
                            Frequency<ScoreDots score={rfm.frequencyScore} />
                        </div>
                        <div style={{ fontSize: 13, color: T.t2, marginTop: 4 }}>
                            {/* null, not 0 — a single-visit customer has no gap to average. */}
                            {rfm.avgDaysBetweenVisits == null
                                ? "Only one visit so far"
                                : `Every ${rfm.avgDaysBetweenVisits} days on average`}
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: 10.5, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>
                            Monetary<ScoreDots score={rfm.monetaryScore} />
                        </div>
                        {/* CUST-3.1: Monetary is spend - "total_spend = SUM(order_total)",
                            "$4,820 → M=5" - not spend less refunds. Net spend lives only in
                            the Order Log footer (CUST-7.3); here it went negative for a
                            refunded order (QA #34). */}
                        <div style={{ fontSize: 13, color: T.t2, marginTop: 4 }}>{money(profile.totalSpend)} spent</div>
                    </div>
                </div>
            </SectionCard>

            {/* ── visit timeline (CUST-7.2, QA #13) — above the log, per CUST-7.3 ── */}
            {fetchVisitPage && (
                <VisitTimeline key={data.customerId} fetchPage={fetchVisitPage} totalOrders={orderLog.totalOrders} visits={profile.visits} money={money} />
            )}

            {/* ── order log (CUST-16) ──────────────────────────────────── */}
            <SectionCard title="Order Log"
                /* "N orders" is the WHOLE log; with a search it also says how many matched. */
                subtitle={`${orderLog.totalOrders.toLocaleString()} orders${
                    orderSearch && orderLog.matchingOrders != null
                        ? ` · ${orderLog.matchingOrders.toLocaleString()} match`
                        : ""
                } · full history, not the selected period`} icon="receipt_long"
                right={(onOrderSearchInput || onOrderPageSize || fetchAllOrders) && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {/* QA #57 - the Directory's controls, same components: a search (order no,
                        channel or item) and rows per page. Both are request parameters, so they
                        act on the WHOLE log, not the page on screen. */}
                    {onOrderSearchInput && (
                        <CustomerSearchBox value={orderSearchInput ?? ""} onChange={onOrderSearchInput} placeholder="Search order no, item or channel" />
                    )}
                    {onOrderPageSize && orderPageSize != null && (
                        <PageSizeSelect value={orderPageSize} onChange={onOrderPageSize} options={CUST_CONFIG.directoryPageSizes} allowAll={false} />
                    )}
                    {fetchAllOrders && (
                    /* QA #12 — the order log is a table, so it exports like every other
                       report table: CSV / Excel / PDF of the WHOLE log, same columns as
                       on screen. One customer at one outlet, so no "All outlets" scope. */
                    <ReportSectionExportButton
                        title={`Order Log — ${profile.name ?? "Unnamed customer"}`}
                        filename={`${(profile.name ?? "customer").trim().replace(/[^A-Za-z0-9]+/g, "_") || "customer"}_order_log_${new Date().toISOString().slice(0, 10)}`}
                        dateRangeLabel={`All orders up to ${day(data.endDate)}`}
                        outletScoped={false}
                        disabled={orderLog.totalOrders === 0}
                        buildExport={async () => {
                            const orders = await fetchAllOrders();
                            /* QA #28 — same convention as Snapshot / Sales: the currency
                               symbol sits on EVERY money cell (`formatMoneyForExport`, the
                               active outlet's symbol) rather than once in the header, so a
                               file never states one currency for an outlet that uses
                               another; and the table ends in a Total row (`totalRow`). */
                            const money = (n: number) => formatMoneyForExport(n);
                            const less = (n: number) => (n ? `-${formatMoneyForExport(n)}` : "—");
                            const round2 = (n: number) => Math.round(n * 100) / 100;
                            const sum = (pick: (o: (typeof orders)[number]) => number) =>
                                round2(orders.reduce((acc, o) => acc + (pick(o) || 0), 0));
                            return {
                                totalRow: true,
                                headers: ["Date", "Time", "Order No", "Channel", "Items", "Item Total", "Discount", "Total", "Refunded", "Cancellation Status", "Order Status"],
                                rows: [
                                    ...orders.map((o) => [
                                        day(o.date), clock(o.time), o.orderNo, o.channel ?? "—",
                                        o.items.length ? o.items.map((i) => `${i.name} ×${i.quantity}`).join(", ") : "item details unavailable",
                                        money(o.itemTotal),
                                        less(totalDiscount(o)),
                                        money(o.grandTotal),
                                        less(o.refundAmount),
                                        o.cancellation === "full" ? "Cancelled" : o.cancellation === "partial" ? "Partial" : "—",
                                        o.orderStatusLabel ?? "—",
                                    ]),
                                    // The column sums of the rows above - every row, as listed.
                                    [
                                        "Total", "", `${orders.length.toLocaleString()} order${orders.length === 1 ? "" : "s"}`, "", "",
                                        money(sum((o) => o.itemTotal)),
                                        less(sum(totalDiscount)),
                                        money(sum((o) => o.grandTotal)),
                                        less(sum((o) => o.refundAmount)),
                                        "", "",
                                    ],
                                ],
                            };
                        }}
                    />
                    )}
                    </div>
                )}>
                {/* Not while a page / search is in flight: the shimmer rows below
                    stand in until it lands, rather than a "no match" for the
                    previous term flashing up first. */}
                {orderLog.content.length === 0 && !logLoading ? (
                    <div style={{ padding: "22px 12px", textAlign: "center", color: T.t3, fontSize: 12.5 }}>
                        {/* A search with no match is not "no orders": the customer has them. */}
                        {orderSearch ? (
                            <>
                                No orders match “{orderSearch}”.{" "}
                                {onOrderSearchInput && (
                                    <span onClick={() => onOrderSearchInput("")} style={{ color: T.accent, cursor: "pointer", fontWeight: 600 }}>Clear search</span>
                                )}
                            </>
                        ) : (
                            <>No orders on or before {day(data.endDate)}.</>
                        )}
                    </div>
                ) : (
                    <>
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
                                <thead>
                                    <tr>
                                        {ORDER_LOG_COLUMNS.map(({ label, key }) => {
                                            const sortable = !!key && !!onOrderSort;
                                            const active = sortable && orderSort?.key === key;
                                            return (
                                                <th key={label} onClick={sortable ? () => onOrderSort!(key!) : undefined}
                                                    style={{ ...thMini, cursor: sortable ? "pointer" : "default" }}>
                                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                                        {label}
                                                        {active && <Icon name={orderSort!.dir === "asc" ? "arrow_upward" : "arrow_downward"} size={12} color={T.accent} />}
                                                    </span>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {logLoading
                                        ? Array.from({ length: Math.min(orderLog.content.length || 8, 8) }).map((_, i) => (
                                            <tr key={`skeleton-${i}`} style={{ borderBottom: `1px solid ${T.divider}` }}>
                                                {ORDER_LOG_COLUMNS.map(({ label }) => (
                                                    <td key={label} style={tdMini}><Bone w={label === "Items" ? 160 : 52} h={12} r={4} /></td>
                                                ))}
                                            </tr>
                                        ))
                                        : orderLog.content.map((o) => (
                                            <OrderRow key={o.orderId} order={o} money={money} />
                                        ))}
                                </tbody>
                            </table>
                        </div>
                        {/* CUST-7.3 summary footer, over the WHOLE log - not this page (QA #13).
                            Money covers non-cancelled orders; cancelled ones are counted on their own. */}
                        {orderLog.summary && orderSearch && (
                            <div style={{ fontSize: 11, color: T.t3, marginTop: 12 }}>
                                The summary below covers the customer's full order history, not just the orders matching your search.
                            </div>
                        )}
                        {orderLog.summary && (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 14, marginTop: 12, padding: "12px 14px", borderRadius: 10, background: T.bg, border: `1px solid ${T.cardBorder}` }}>
                                <Stat label="Total Orders" value={orderLog.summary.totalOrders.toLocaleString()} />
                                <Stat label="Gross Spend" value={money(orderLog.summary.grossSpend)} />
                                <Stat label="Total Discounts" value={orderLog.summary.totalDiscounts ? `-${money(orderLog.summary.totalDiscounts)}` : "—"} />
                                <Stat label="Total Refunds" value={orderLog.summary.totalRefunds ? `-${money(orderLog.summary.totalRefunds)}` : "—"}
                                    tone={orderLog.summary.totalRefunds ? T.red : undefined} />
                                <Stat label="Net Spend" value={money(orderLog.summary.netSpend)} />
                                <Stat label="Total Cancelled Orders" value={orderLog.summary.cancelledOrders.toLocaleString()} />
                            </div>
                        )}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                            <div style={{ fontSize: 11.5, color: T.t3 }}>
                                Tap a row with items to see the lines, refunds and reasons.
                            </div>
                            <PaginationBar page={orderLog.page} totalPages={orderLog.totalPages} onChange={onPage} />
                        </div>
                    </>
                )}
            </SectionCard>
        </div>
    );
}
